import React from 'react';
import { motion } from 'framer-motion';

/**
 * Wiederverwendbarer Hero im Liedzeit-Tiefblau — identischer Look wie die Startseite:
 * Navy-Radialverlauf, driftendes Leuchten, aufsteigende Bernstein-Partikel,
 * Bernstein-Eyebrow, Serif-Titel, optional Untertitel/Beschreibung/Buttons.
 *
 * Nutzung Unterseite:  <Hero title={t.nav.services} />
 * Nutzung Startseite:  <Hero size="lg" eyebrow="…" title={…} subtitle={…} description={…}>…Buttons…</Hero>
 */

const PARTICLES = [
  { l: '12%', s: 8, d: 9, delay: -1 },
  { l: '24%', s: 5, d: 11, delay: -4 },
  { l: '38%', s: 10, d: 8, delay: -2.5 },
  { l: '51%', s: 6, d: 12, delay: -6 },
  { l: '63%', s: 7, d: 10, delay: -0.5 },
  { l: '74%', s: 5, d: 13, delay: -7.5 },
  { l: '86%', s: 9, d: 9.5, delay: -3.5 },
  { l: '44%', s: 4, d: 14, delay: -9 },
  { l: '69%', s: 5, d: 11.5, delay: -5 },
];

const ease = [0.22, 1, 0.36, 1];

export default function Hero({
  eyebrow,
  title,
  subtitle,
  description,
  children,
  gradientClass = 'hero-gradient-services',
  size = 'sm',
}) {
  const big = size === 'lg';
  return (
    <section
      className={`py-20 relative flex items-center ${gradientClass}`}
      style={{ minHeight: big ? '520px' : '380px' }}
    >
      <div className="absolute inset-0 bg-brand-dark/50"></div>

      {/* Ambient: driftendes Leuchten */}
      <div aria-hidden="true" data-hero-glow className="absolute inset-0 pointer-events-none"
        style={{ backgroundImage: 'radial-gradient(circle at 82% 20%, rgba(39,80,140,0.55), transparent 46%)', animation: 'lzDrift 17s ease-in-out infinite alternate' }}></div>
      <div aria-hidden="true" data-hero-glow className="absolute inset-0 pointer-events-none"
        style={{ backgroundImage: 'radial-gradient(circle at 18% 82%, rgba(196,168,98,0.16), transparent 42%)', animation: 'lzGlow 13s ease-in-out infinite alternate' }}></div>

      {/* Ambient: aufsteigende Bernstein-Partikel */}
      <div aria-hidden="true" className="absolute inset-0 overflow-hidden pointer-events-none">
        {PARTICLES.map((p, i) => (
          <span key={i} data-hero-particle style={{
            position: 'absolute', bottom: '-16px', left: p.l,
            width: p.s, height: p.s, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(252,211,77,.9), rgba(252,211,77,0) 70%)',
            animation: `lzFloat ${p.d}s linear infinite`, animationDelay: `${p.delay}s`,
          }}></span>
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
        <div className="max-w-4xl mx-auto">
          {eyebrow && (
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease }}
              className="text-xs font-bold uppercase tracking-eyebrow text-amber-300 mb-5"
            >
              {eyebrow}
            </motion.p>
          )}
          {title && (
            <motion.h1
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.08, ease }}
              className={`font-serif font-bold text-white ${big ? 'text-4xl md:text-6xl' : 'text-4xl md:text-5xl'}`}
            >
              {title}
            </motion.h1>
          )}
          {subtitle && (
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.16, ease }}
              className="font-serif italic text-xl md:text-2xl text-amber-100 mt-6"
            >
              {subtitle}
            </motion.p>
          )}
          {description && (
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.24, ease }}
              className="text-lg text-stone-300 mt-6 max-w-2xl mx-auto leading-relaxed"
            >
              {description}
            </motion.p>
          )}
          {children && (
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.32, ease }}
              className="mt-10 flex flex-col sm:flex-row gap-4 justify-center"
            >
              {children}
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}
